import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/app/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Search, Star, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", contact_person: "", mobile: "", email: "", gst_number: "",
  address: "", district: "", state: "Kerala", payment_terms: "", credit_days: 0,
  preferred: false, active: true, notes: "",
};

export default function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/purchasing/vendors").then((r) => setVendors(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return vendors;
    return vendors.filter((v) => [v.name, v.contact_person, v.mobile, v.email, v.gst_number, v.district]
      .some((x) => x && String(x).toLowerCase().includes(s)));
  }, [vendors, q]);

  const del = async (v) => {
    if (!window.confirm(`Delete vendor "${v.name}"?`)) return;
    try {
      await api.delete(`/purchasing/vendors/${v.id}`);
      toast.success("Vendor deleted"); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="pv-vendors-page">
      <PageHeader
        eyebrow="Purchasing"
        title="Vendor Master"
        subtitle="Multi-brand suppliers with GST, credit terms and preferred flag."
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-11 px-6" data-testid="pv-new-vendor-btn">
                <Plus className="w-4 h-4 mr-2" /> New Vendor
              </Button>
            </DialogTrigger>
            {open && <VendorDialog editing={editing} onDone={() => { setOpen(false); setEditing(null); load(); }} />}
          </Dialog>
        }
      />
      <div className="p-4 sm:p-8">
        <div className="mb-4 relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="rounded-none h-10 pl-9" placeholder="Search vendor, GST, contact…" data-testid="pv-vendor-search" />
        </div>
        <div className="border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>{["", "Vendor", "Contact", "GST", "Terms", "District", "Status", ""].map((h, i) => (
                <th key={i} className="text-left label-eyebrow !text-[10px] px-4 py-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {filtered.map((v) => (
                <tr key={v.id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`pv-vendor-row-${v.id}`}>
                  <td className="px-4 py-3">{v.preferred && <Star className="w-4 h-4 text-[#F59E0B] fill-[#F59E0B]" />}</td>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{v.name}</div>
                    {v.email && <div className="text-xs text-[#4B5563]">{v.email}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {v.contact_person || "—"}
                    {v.mobile && <div className="text-[#4B5563]">{v.mobile}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono">{v.gst_number || "—"}</td>
                  <td className="px-4 py-3 text-xs">{v.payment_terms || "—"}<div className="text-[#9CA3AF]">Credit: {v.credit_days || 0}d</div></td>
                  <td className="px-4 py-3 text-xs">{v.district || "—"}<div className="text-[#9CA3AF]">{v.state || ""}</div></td>
                  <td className="px-4 py-3 text-xs">
                    <span className={`px-2 py-0.5 uppercase tracking-widest font-bold text-[9px] ${v.active ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#F3F4F6] text-[#6B7280]"}`}>
                      {v.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => { setEditing(v); setOpen(true); }} className="text-[#4B5563] hover:text-[#002FA7] mr-3" data-testid={`pv-vendor-edit-${v.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(v)} className="text-[#9CA3AF] hover:text-[#DC2626]" data-testid={`pv-vendor-del-${v.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={8} className="text-center text-sm text-[#9CA3AF] py-12">No vendors match your search.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function VendorDialog({ editing, onDone }) {
  const isEdit = !!editing;
  const [f, setF] = useState({ ...EMPTY, ...(editing || {}) });
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });

  const submit = async () => {
    if (!f.name?.trim()) { toast.error("Vendor name required"); return; }
    const payload = { ...f, credit_days: Number(f.credit_days) || 0 };
    try {
      if (isEdit) await api.patch(`/purchasing/vendors/${editing.id}`, payload);
      else await api.post("/purchasing/vendors", payload);
      toast.success(isEdit ? "Vendor updated" : "Vendor created"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-black text-2xl tracking-tight">{isEdit ? "Edit Vendor" : "New Vendor"}</DialogTitle></DialogHeader>
      <div className="space-y-3 pt-2">
        <F label="Vendor Name *" value={f.name} onChange={set("name")} testid="pv-v-name" />
        <div className="grid grid-cols-2 gap-2">
          <F label="Contact Person" value={f.contact_person} onChange={set("contact_person")} />
          <F label="Mobile" value={f.mobile} onChange={set("mobile")} />
        </div>
        <F label="Email" value={f.email} onChange={set("email")} />
        <F label="GST Number" value={f.gst_number} onChange={set("gst_number")} testid="pv-v-gst" />
        <F label="Address" value={f.address} onChange={set("address")} />
        <div className="grid grid-cols-2 gap-2">
          <F label="District" value={f.district} onChange={set("district")} />
          <F label="State" value={f.state} onChange={set("state")} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <F label="Payment Terms (e.g. Net 30)" value={f.payment_terms} onChange={set("payment_terms")} />
          <F label="Credit Days" type="number" value={f.credit_days} onChange={set("credit_days")} />
        </div>
        <div className="flex items-center gap-4 pt-1">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!f.preferred} onChange={(e) => setF({ ...f, preferred: e.target.checked })} className="w-4 h-4 accent-[#F59E0B]" data-testid="pv-v-preferred" />
            Preferred vendor
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="w-4 h-4 accent-[#10B981]" data-testid="pv-v-active" />
            Active
          </label>
        </div>
        <div>
          <Label className="label-eyebrow">Notes</Label>
          <Textarea value={f.notes || ""} onChange={set("notes")} rows={2} className="rounded-none mt-2" />
        </div>
        <Button onClick={submit} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-11 uppercase tracking-widest text-xs font-bold" data-testid="pv-v-submit">
          {isEdit ? "Save Changes" : "Create Vendor"}
        </Button>
      </div>
    </DialogContent>
  );
}

function F({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <Input type={type} value={value || ""} onChange={onChange} className="rounded-none mt-2 h-10" data-testid={testid} />
    </div>
  );
}
